import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { FormHelperText, InputLabel, styled } from '@mui/material';
import {
  DatePickerProps,
  LocalizationProvider,
  DatePicker as MuiDatePicker,
  PickerChangeHandlerContext,
  DateValidationError,
} from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment, { Moment } from 'moment';

import { ActionBar } from '@/components';

import { BaseFieldProps } from '../types';

export const DatePickerRoot = styled('div')(() => ({
  flex: 1,
  minWidth: '100%',
  maxWidth: '100%',
  marginBottom: 8,
  '& .MuiFormControl-root': {
    width: '100%',
  },
  '& .MuiPickersLayout-root': {
    display: 'inherit',
  },
}));

export interface BaseDatePickerProps
  extends DatePickerProps<Moment>,
    BaseFieldProps {
  initialCfsDateTime: Moment | null;
  setDateManuallyChanged: Dispatch<SetStateAction<boolean>>;
  onNowClick?: (value: Moment) => void;
  onClearClick?: () => void;
}

export const DatePicker = forwardRef<HTMLInputElement, BaseDatePickerProps>(
  (props, ref) => {
    const {
      label,
      error,
      onChange,
      value,
      initialCfsDateTime,
      setDateManuallyChanged,
      onNowClick,
      onClearClick,
      ...rest
    } = props;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [selectedDate, setSelectedDate] = useState<Moment | null>(
      value || null,
    );
    const [open, setOpen] = useState(false);

    const setRefs = useCallback(
      (element: HTMLInputElement | null) => {
        inputRef.current = element;

        if (typeof ref === 'function') {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref],
    );

    const handleNowClick = () => {
      if (setDateManuallyChanged) {
        setDateManuallyChanged(true);
      }
      const currentDate = moment();
      setSelectedDate(currentDate);
      onChange?.(currentDate, { validationError: null });
      onNowClick?.(currentDate);
    };

    const handleClear = () => {
      setSelectedDate(initialCfsDateTime);
      onChange?.(initialCfsDateTime, { validationError: null });
      onClearClick?.();
      setOpen(false);
    };

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          const currentDate = moment();
          if (onChange) {
            const context: PickerChangeHandlerContext<DateValidationError> = {
              validationError: null,
            };
            onChange(currentDate, context);
          }
        }
      };

      const inputElement = inputRef.current;
      if (inputElement) {
        inputElement.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        if (inputElement) {
          inputElement.removeEventListener('keydown', handleKeyDown);
        }
      };
    }, [onChange]);

    useEffect(() => {
      setSelectedDate(value || null);
    }, [value]);

    return (
      <DatePickerRoot>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          {label && <InputLabel>{label}</InputLabel>}
          <MuiDatePicker
            {...rest}
            value={selectedDate}
            inputRef={setRefs}
            sx={{
              mt: 0,
            }}
            onChange={onChange}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            slots={{
              actionBar: () => (
                <ActionBar
                  onNowClick={handleNowClick}
                  onClear={handleClear}
                  onOk={() => setOpen(false)}
                />
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  '&.MuiPickersLayout-root': {
                    display: 'inherit',
                    flexDirection: 'column',
                  },
                },
              },
            }}
          />
          {error && <FormHelperText error>{error}</FormHelperText>}
        </LocalizationProvider>
      </DatePickerRoot>
    );
  },
);

DatePicker.displayName = 'Custom Date Picker';
